import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { FiChevronLeft } from "react-icons/fi";
import { get as commsGet } from "../../../service/comms/commsApi";
import {
  getCoachAnalytics,
  classifyOversightError,
} from "../../../service/comms/oversightAnalyticsService";
import { presetRange, resolvedId } from "../../../utils/oversightFormat";
import { DateRangePreset } from "../../../common/comms/DateRangePreset";
import { EmptyState } from "../../../common/comms/EmptyState";
import { RetryBanner } from "../../../common/comms/RetryBanner";
import { SkeletonRows } from "../../../common/comms/SkeletonRows";
import { AvatarRing } from "../../../common/comms/AvatarRing";
import { useCommsIdentity } from "../../../hooks/comms/useCommsIdentity";
import { OverviewTab } from "./overview/OverviewTab";
import { PlansTab } from "./plans/PlansTab";
import { SessionDays } from "./sessions/SessionDays";
import { ClientsTab } from "./clients/ClientsTab";
import { useCoachClients } from "./clients/useCoachClients";
import { useCoachSupportMetrics } from "./overview/useCoachSupportMetrics";

const DEFAULT_PRESET_DAYS = 30;
const WIDE_PRESET_DAYS = 90;

/**
 * Overview | Plans | Sessions | Clients.
 *
 * Driven by `?tab=` rather than local state so a manager can link a colleague
 * straight at the tab they are talking about — the same pattern the `/coaches`
 * console already uses.
 */
const TABS = [
  { id: "overview", label: "Overview" },
  { id: "plans", label: "Plans" },
  { id: "sessions", label: "Sessions" },
  { id: "clients", label: "Clients" },
];

const TAB_IDS = TABS.map((t) => t.id);

const GRANULARITIES = [
  { value: "day", label: "Day" },
  { value: "week", label: "Week" },
];

/**
 * Per-coach oversight/analytics drill-down page (`/coaches/:coachId`).
 *
 * Answers three questions, one per tab: is this coach talking to their clients
 * often enough (Overview), when do they actually work (Sessions), and where has
 * the coaching got to for each client (Clients). Plans — is their plan work any
 * good — is designed but not yet measured; it says so rather than showing
 * invented numbers.
 *
 * Owns the date-range preset (default 30d), the day/week granularity toggle and
 * the client roster, fetching `GET /oversight/analytics/coaches/{coach_id}` on
 * mount and whenever the range or granularity changes. The roster is fetched
 * once here and shared with both the Overview cadence panel and the Clients
 * tab, so the two can never disagree.
 *
 * `:coachId` may be a user_id or an employee_id — it's forwarded to the API
 * verbatim (the backend resolves it). `resolved_coach_user_id` from the
 * response (falling back to `resolvedId(payload)`) is threaded down so child
 * sections key off the canonical id rather than the raw route param.
 *
 * Error handling follows the oversight API error contract: a 403 on this single
 * coach resource means "outside your report chain" (a scoping decision, not a
 * "you're not staff" signal) — it renders a full-panel EmptyState with a way
 * back to `/coaches` and does NOT reprobe staff status. 502/504/network and
 * other errors render a RetryBanner with a working retry.
 */
export default function CoachDetail() {
  const { coachId } = useParams();
  const navigate = useNavigate();
  const { getCoachName } = useCommsIdentity();
  const [searchParams, setSearchParams] = useSearchParams();

  const requestedTab = searchParams.get("tab");
  const activeTab = TAB_IDS.includes(requestedTab) ? requestedTab : "overview";

  // Carried in the URL (not state) so the scope survives a shared link, a
  // reload and the back button. Undefined means the server's default
  // ("reports"), so the common case sends no param at all.
  const scope = searchParams.get("scope") === "company" ? "company" : undefined;

  const [presetDays, setPresetDays] = useState(DEFAULT_PRESET_DAYS);
  const [range, setRange] = useState(() => presetRange(DEFAULT_PRESET_DAYS));
  const [granularity, setGranularity] = useState("day");

  const [payload, setPayload] = useState(null);
  const [resolved, setResolved] = useState(null);

  const [loading, setLoading] = useState(true);
  /** @type {[('forbidden'|'gateway'|'other'|null), Function]} */
  const [errorKind, setErrorKind] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  // Active-cleanup fetch: refires on coachId, range, granularity, or retry.
  useEffect(() => {
    if (!coachId) return undefined;
    let active = true;
    setLoading(true);
    setErrorKind(null);
    getCoachAnalytics(commsGet, coachId, { ...range, granularity, scope })
      .then((data) => {
        if (!active) return;
        setPayload(data);
        setResolved(data?.resolved_coach_user_id ?? resolvedId(data));
      })
      .catch((err) => {
        if (!active) return;
        setErrorKind(classifyOversightError(err));
        setPayload(null);
        setResolved(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [coachId, range, granularity, scope, reloadToken]);

  const effectiveId = resolved || coachId;
  const roster = useCoachClients(effectiveId);
  const support = useCoachSupportMetrics(effectiveId, range, scope);

  // Tabs that have ever been opened. Plans mounts on first visit and then stays
  // mounted behind `hidden`, so returning to it does not re-request. It is NOT
  // fetched up front like the roster and support metrics: it is not the default
  // tab, so eager-loading it would put a request on the critical path for every
  // visitor who never opens it.
  const [openedTabs, setOpenedTabs] = useState({ overview: true });
  useEffect(() => {
    setOpenedTabs((cur) => (cur[activeTab] ? cur : { ...cur, [activeTab]: true }));
  }, [activeTab]);

  // A range change makes any retained tab's data wrong. Remounting Plans is the
  // simplest correct invalidation — its own effect keys on `range` too, but the
  // key forces the retained subtree to drop rather than briefly showing the
  // previous window's numbers.
  const rangeKey = `${range.date_from}|${range.date_to}`;

  const handlePresetChange = ({ days, date_from, date_to }) => {
    setPresetDays(days);
    setRange({ date_from, date_to });
    // Daily buckets over 90 days are too dense to read — nudge to weekly.
    // The user can still switch back to Day afterward.
    if (days === WIDE_PRESET_DAYS && granularity === "day") {
      setGranularity("week");
    }
  };

  const handleRetry = () => setReloadToken((t) => t + 1);

  const handleBack = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate("/coaches");
    }
  };

  // Rewrites `?tab=` while preserving every other search param.
  const handleTabChange = (tabId) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", tabId);
    setSearchParams(next, { replace: true });
  };

  const totals = payload?.totals;
  const colorIndex = effectiveId ? String(effectiveId).charCodeAt(0) % 8 : 0;
  const coachName =
    payload?.coach_name ||
    getCoachName(effectiveId) ||
    (effectiveId ? `#${String(effectiveId).slice(-6)}` : "Coach");
  // Presence is live (unlike the nightly-rollup metrics) but may not be on
  // the payload at all — only render the dot when it's explicitly known.
  const isOnlineNow = totals?.is_online_now === true;

  return (
    <div className="p-4 sm:p-6 bg-gray-50 min-h-[calc(100vh-4.5rem)]">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
        <div className="flex items-start gap-3 min-w-0 flex-wrap">
          <button
            type="button"
            onClick={handleBack}
            aria-label="Back to coaches"
            className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 hover:text-gray-700 hover:bg-gray-50 flex-shrink-0"
          >
            <FiChevronLeft className="w-5 h-5" aria-hidden="true" />
          </button>
          <AvatarRing
            name={coachName}
            colorIndex={colorIndex}
            presence={isOnlineNow ? "online" : undefined}
            size="lg"
          />
          <div className="min-w-0">
            <h1 className="text-xl font-bold text-gray-900 truncate">{coachName}</h1>
            <p className="text-xs text-gray-400">
              Aggregates from nightly rollup · presence is live
            </p>
            <p className="mt-1.5 text-xs text-gray-700 max-w-[70ch]">
              In plain terms: are they talking to their clients often enough, and is anything
              slipping?
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 flex-shrink-0">
          <DateRangePreset
            value={presetDays}
            onChange={handlePresetChange}
            className="w-full sm:w-auto"
          />
          <div
            role="group"
            aria-label="Chart granularity"
            className="inline-flex rounded-lg border border-gray-200 p-0.5 bg-white"
          >
            {GRANULARITIES.map((g) => {
              const isActive = g.value === granularity;
              return (
                <button
                  key={g.value}
                  type="button"
                  aria-pressed={isActive}
                  onClick={() => setGranularity(g.value)}
                  className={`min-h-[44px] flex-1 px-3 rounded-md text-xs font-semibold transition-colors ${
                    isActive
                      ? "bg-light-orange text-orange-primary border border-orange-200"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {g.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {errorKind === "forbidden" ? (
        <EmptyState
          title="This coach is outside your report chain"
          hint="You don't have access to this coach's analytics."
          action={
            <button
              type="button"
              onClick={() => navigate("/coaches")}
              className="min-h-[44px] px-4 text-sm font-medium text-white rounded-lg bg-orange-primary hover:opacity-90"
            >
              Back to coaches
            </button>
          }
        />
      ) : errorKind === "gateway" ? (
        <RetryBanner onRetry={handleRetry} />
      ) : errorKind === "other" ? (
        <RetryBanner message="Something went wrong loading coach analytics." onRetry={handleRetry} />
      ) : (
        <>
          <div className="flex gap-1 border-b border-gray-200 mb-4 overflow-x-auto" role="tablist">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`min-h-[44px] px-4 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? "border-orange-primary text-orange-primary"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="space-y-4">
              <SkeletonRows variant="metrics" count={4} />
              <SkeletonRows variant="rows" count={5} />
            </div>
          ) : (
            <>
              {/* Plans is the only tab that owns a fetch, so it is the only one
                  kept mounted — hidden rather than unmounted, so coming back to
                  it costs nothing. The others are presentational and cheap to
                  remount. */}
              {openedTabs.plans && (
                <div className={activeTab === "plans" ? "" : "hidden"}>
                  <PlansTab key={rangeKey} coachId={effectiveId} range={range} scope={scope} />
                </div>
              )}

              {activeTab === "sessions" ? (
                <SessionDays
                  series={payload?.series}
                  granularity={granularity}
                  // Absent from the payload today, so the coach-clock label stays inert.
                  coachTimeZone={payload?.coach_timezone ?? null}
                />
              ) : activeTab === "clients" ? (
                <ClientsTab
                  clients={roster.clients}
                  loading={roster.loading}
                  errorKind={roster.errorKind}
                  onRetry={roster.retry}
                />
              ) : activeTab === "overview" ? (
                <OverviewTab
                  payload={payload}
                  granularity={granularity}
                  clients={roster.clients}
                  clientsLoading={roster.loading}
                  onOpenPlans={() => handleTabChange("plans")}
                  support={support}
                />
              ) : null}
            </>
          )}
        </>
      )}
    </div>
  );
}
